import { useSyncExternalStore } from "react";
import { colorToRgbTriplet, type TenantBranding } from "@match/shared";

/*
 * IL MARCHIO CHE SI VEDE: quello salvato, o la bozza della pagina Branding.
 *
 * Un posto solo, perche' tre cose lo leggono — le variabili CSS, la barra
 * laterale (nome e logo) e il titolo della scheda — e l'anteprima dal vivo
 * deve cambiarle tutte insieme. La pagina Branding scrive la bozza con
 * `setPreviewBranding`; «Annulla», o uscire dalla pagina, la toglie e torna
 * quello salvato.
 */

export const DEFAULT_NAME = "Match Trading";

let saved: TenantBranding | null = null;
let preview: TenantBranding | null = null;
const listeners = new Set<() => void>();

const shown = () => preview ?? saved;

function publish() {
  apply(shown());
  for (const l of listeners) l();
}

export function setSavedBranding(b: TenantBranding | null) {
  saved = b;
  publish();
}

export function setPreviewBranding(b: TenantBranding | null) {
  preview = b;
  publish();
}

export function getSavedBranding(): TenantBranding | null {
  return saved;
}

/** Il marchio che si vede adesso: la bozza se c'e', altrimenti quello salvato. */
export function useShownBranding(): TenantBranding | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    shown,
  );
}

function apply(b: TenantBranding | null) {
  applyTokens(b);
  applyFavicon(b?.faviconUrl);
  document.title = b?.name ?? DEFAULT_NAME;
}

/** Le variabili CSS scritte dall'ultimo marchio applicato, per poterle togliere. */
let applicate: string[] = [];

/**
 * Applica i token colore del tenant sovrascrivendo le variabili CSS.
 *
 * **Toglie PRIMA quelle di prima**, e non e' pulizia: da quando si entra tutti
 * dallo stesso indirizzo, la schermata di login e' condivisa. Un marchio che
 * resta addosso dopo l'uscita direbbe al prossimo che arriva da quel browser
 * di che gestore era chi c'era prima.
 *
 * **Nel CSS entrano solo tre numeri**: `colorToRgbTriplet` e' la stessa
 * grammatica con cui il backend rifiuta (packages/shared/src/color.ts), e cio'
 * che non la passa non si scrive. La stringa salvata non arriva mai al foglio
 * di stile.
 */
function applyTokens(branding: TenantBranding | null) {
  const root = document.documentElement;
  for (const name of applicate) root.style.removeProperty(name);
  applicate = [];
  if (!branding?.tokens) return;
  for (const [name, value] of Object.entries(branding.tokens)) {
    const raw = value.dark ?? value.light;
    if (!raw) continue;
    const rgb = colorToRgbTriplet(raw);
    if (rgb && /^[a-z-]+$/.test(name)) {
      root.style.setProperty(`--${name}`, rgb);
      applicate.push(`--${name}`);
    }
  }
}

/**
 * L'icona del browser, dal marchio del tenant. `null` la toglie, invece di
 * lasciare quella dell'ultimo tenant uscito sullo schermo di login condiviso.
 */
function applyFavicon(url: string | null | undefined) {
  const id = "tenant-favicon";
  const esistente = document.getElementById(id);
  if (!url) {
    esistente?.remove();
    return;
  }
  const link = (esistente as HTMLLinkElement | null) ?? document.createElement("link");
  link.id = id;
  link.rel = "icon";
  link.href = url;
  if (!esistente) document.head.appendChild(link);
}
